<template>
  <Modal
    :title="isEdit ? `Edit ${programName}` : `New ${programName}`"
    :saving="saving"
    cancel-button
    @hide-modal="$emit('hide-modal')"
    @submit="handleSubmit"
  >
    <div data-cy="schedule-form">
      <div class="mb-3">
        <label class="form-label">Name</label>
        <input
          v-model="form.name"
          type="text"
          class="form-control"
          required
          data-cy="schedule-name"
        />
      </div>
      <div class="mb-3">
        <label class="form-label">Description</label>
        <input
          v-model="form.description"
          type="text"
          class="form-control"
          data-cy="schedule-description"
        />
      </div>

      <h6 class="mt-4 mb-3">{{ intervalNamePlural }}</h6>
      <ProgressBillingInstallmentRow
        v-for="(installment, idx) in visibleInstallments"
        :key="idx"
        :installment="installment"
        :removable="visibleInstallments.length > 2"
        @remove="removeInstallment(idx)"
        @update:label="installment.label = $event"
        @update:percentage="updatePercentage(installment, $event)"
      />

      <div class="d-flex justify-content-between align-items-center mt-2">
        <ItemAdder
          slim
          transparent
          :text="`Add ${intervalName}`"
          class="flex-1 mr-3"
          data-cy="add-installment"
          @click="addInstallment"
        />
        <span
          class="small whitespace-nowrap"
          :class="percentageTotal === 100 ? 'text-success' : 'text-danger'"
        >
          Total: {{ percentageTotal }}%
        </span>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mt-3">
        {{ errorMessage }}
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { useStore } from "vuex";
import { trackEvent } from "@/lib/pendo";

import ItemAdder from "@/components/ItemAdder.vue";
import Modal from "@/components/Modal.vue";
import { useProgressBillingLabels } from "@/composables/useProgressBillingLabels";
import ProgressBillingInstallmentRow from "./ProgressBillingInstallmentRow.vue";

const { programName, intervalName, intervalNamePlural } =
  useProgressBillingLabels();

interface InstallmentForm {
  id?: number;
  label: string;
  percentage: number | null;
  position: number;
  _destroy?: boolean;
}

const props = defineProps<{
  scheduleId?: number | string | null;
}>();

const emit = defineEmits<{
  "hide-modal": [];
  reload: [];
}>();

const store = useStore();

const isEdit = computed(() => !!props.scheduleId);
const saving = ref(false);
const errorMessage = ref("");

const form = reactive({
  name: "",
  description: "",
  installments: [
    { label: "Deposit", percentage: 50, position: 0 },
    { label: "Final", percentage: 50, position: 1 }
  ] as InstallmentForm[]
});

const visibleInstallments = computed(() =>
  form.installments.filter((i) => !i._destroy)
);

const percentageTotal = computed(() => {
  return visibleInstallments.value.reduce(
    (sum, i) => sum + (Number(i.percentage) || 0),
    0
  );
});

function updatePercentage(installment: InstallmentForm, value: number) {
  installment.percentage = value;
  // With exactly two lines the split must total 100%, so editing one
  // auto-fills the remainder on the other.
  if (visibleInstallments.value.length === 2) {
    const other = visibleInstallments.value.find((i) => i !== installment);
    if (other) {
      other.percentage = Math.max(0, 100 - value);
    }
  }
}

function addInstallment() {
  form.installments.push({
    label: "",
    percentage: null,
    position: visibleInstallments.value.length
  });
}

function removeInstallment(visibleIdx: number) {
  const installment = visibleInstallments.value[visibleIdx];
  if (installment.id) {
    installment._destroy = true;
  } else {
    const realIdx = form.installments.indexOf(installment);
    form.installments.splice(realIdx, 1);
  }
  reorderPositions();
}

function reorderPositions() {
  let pos = 0;
  for (const inst of form.installments) {
    if (!inst._destroy) {
      inst.position = pos++;
    }
  }
}

async function handleSubmit() {
  if (!form.name.trim()) {
    errorMessage.value = "Name is required";
    return;
  }

  saving.value = true;
  errorMessage.value = "";
  reorderPositions();

  try {
    const params = {
      name: form.name,
      description: form.description,
      installments_attributes: form.installments.map((i) => ({
        ...(i.id ? { id: i.id } : {}),
        label: i.label,
        percentage: i.percentage,
        position: i.position,
        ...(i._destroy ? { _destroy: true } : {})
      }))
    };

    if (isEdit.value) {
      await store.dispatch("updateProgressBillingSchedule", {
        id: Number(props.scheduleId),
        ...params
      });
    } else {
      await store.dispatch("createProgressBillingSchedule", params);
    }
    trackEvent("spb_template_saved", {
      is_new: !isEdit.value,
      installments_count: visibleInstallments.value.length
    });
    emit("reload");
    emit("hide-modal");
  } catch (err: unknown) {
    const error = err as { response?: { data?: { errors?: string[] } } };
    errorMessage.value =
      error.response?.data?.errors?.join(", ") || "Failed to save schedule";
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  if (isEdit.value) {
    const schedule = await store.dispatch(
      "fetchProgressBillingSchedule",
      Number(props.scheduleId)
    );
    form.name = schedule.name;
    form.description = schedule.description || "";
    form.installments = schedule.installments.map(
      (i: {
        id: number;
        label: string;
        percentage: number;
        position: number;
      }) => ({
        id: i.id,
        label: i.label,
        // Server stores percentages as decimals (e.g. 40.0); the inputs are
        // whole-integer, so normalize to match the create/edit-by-typing path.
        percentage: Math.round(i.percentage),
        position: i.position
      })
    );
  }
});
</script>
